import { z } from "zod";
import { ExtraCategories, ExtraCategory } from "../ExtraCategory.enum";

/** Every issue code of the feature. `code` is the contract with backoffice-manager. */
export enum IssueCode {
  CATEGORY_LIMITS_INCLUDED_REQUIRED = "CATEGORY_LIMITS_INCLUDED_REQUIRED",
  CATEGORY_LIMITS_MAX_REQUIRED = "CATEGORY_LIMITS_MAX_REQUIRED",
  CATEGORY_LIMITS_INCLUDED_OUT_OF_RANGE = "CATEGORY_LIMITS_INCLUDED_OUT_OF_RANGE",
  CATEGORY_LIMITS_MAX_OUT_OF_RANGE = "CATEGORY_LIMITS_MAX_OUT_OF_RANGE",
  CATEGORY_LIMITS_INCLUDED_GREATER_THAN_MAX = "CATEGORY_LIMITS_INCLUDED_GREATER_THAN_MAX",
  CATEGORY_LIMITS_NO_ACTIVE_EXTRA = "CATEGORY_LIMITS_NO_ACTIVE_EXTRA",
  CATEGORY_LIMITS_DUPLICATE_BLOCK = "CATEGORY_LIMITS_DUPLICATE_BLOCK",
  CATEGORY_LIMITS_UNKNOWN_CATEGORY = "CATEGORY_LIMITS_UNKNOWN_CATEGORY",
  CATEGORY_LIMITS_EXTRA_NO_INVENTORY_LINE = "CATEGORY_LIMITS_EXTRA_NO_INVENTORY_LINE",
  EXTRA_CATEGORY_UNSUPPORTED_TYPE = "EXTRA_CATEGORY_UNSUPPORTED_TYPE",
  EXTRA_VEHICLE_TYPE_REQUIRED = "EXTRA_VEHICLE_TYPE_REQUIRED",
  EXTRA_VEHICLE_FIELDS_WITHOUT_VEHICLE = "EXTRA_VEHICLE_FIELDS_WITHOUT_VEHICLE",
  EXTRA_VEHICLE_TYPE_INVALID = "EXTRA_VEHICLE_TYPE_INVALID",
  EXTRA_CHANGE_STOPS_COUNTING = "EXTRA_CHANGE_STOPS_COUNTING",
  EXTRA_CHANGE_LEAVES_BLOCK_EMPTY = "EXTRA_CHANGE_LEAVES_BLOCK_EMPTY",
  EXTRA_DEACTIVATION_REMOVES = "EXTRA_DEACTIVATION_REMOVES",
  EXTRA_DEACTIVATION_LEAVES_BLOCK_EMPTY = "EXTRA_DEACTIVATION_LEAVES_BLOCK_EMPTY",
  EXTRA_COVERAGE_LOST = "EXTRA_COVERAGE_LOST",
  EXTRA_CATEGORY_NOT_ENABLED_IN_PROPERTY = "EXTRA_CATEGORY_NOT_ENABLED_IN_PROPERTY",
}

export enum IssueSeverity {
  UNSPECIFIED = "UNSPECIFIED",
  BLOCKING = "BLOCKING",
  WARNING = "WARNING",
}

/** Controls an issue can point to (`IssueTarget.field`). */
export enum IssueField {
  INCLUDED = "included",
  MAX = "max",
  EXTRAS = "extras",
  CATEGORY = "category",
  VEHICLE_TYPE = "vehicleType",
  REQUEST_LICENSE_PLATE = "requestLicensePlate",
  ACTIVE = "active",
  APPLIES_TO = "appliesTo",
}

const MESSAGE_KEY_PREFIX = "extrasCategories.issue.";

const IssueTargetSchema = z.object({
  propertyTicker: z.string().min(1).optional(),
  accommodationId: z.string().min(1).optional(),
  category: z.enum(ExtraCategory).default(ExtraCategory.NONE),
  extraTicker: z.string().min(1).optional(),
  // Kept as a string: an unknown field must not make the whole issue unreadable.
  field: z.string().min(1).optional(),
});

export type IssueTargetProps = z.input<typeof IssueTargetSchema>;

/** Where an issue applies: block (`category`), control (`field`), line (`extraTicker`). */
export class IssueTarget {
  public readonly propertyTicker?: string;
  public readonly accommodationId?: string;
  public readonly category: ExtraCategory;
  public readonly extraTicker?: string;
  public readonly field?: string;

  private constructor(props: z.infer<typeof IssueTargetSchema>) {
    this.propertyTicker = props.propertyTicker;
    this.accommodationId = props.accommodationId;
    this.category = props.category;
    this.extraTicker = props.extraTicker;
    this.field = props.field;
  }

  static of(props: IssueTargetProps = {}): IssueTarget {
    return new IssueTarget(IssueTargetSchema.parse(props));
  }

  static none(): IssueTarget {
    return IssueTarget.of();
  }

  /** `category = NONE` means the issue is not scoped to an accommodation block. */
  isBlockScoped(): boolean {
    return this.category !== ExtraCategory.NONE;
  }

  targetsBlock(category: ExtraCategory): boolean {
    return this.isBlockScoped() && this.category === category;
  }

  targetsField(field: IssueField): boolean {
    return this.field === field;
  }

  targetsExtra(extraTicker: string): boolean {
    return this.extraTicker === extraTicker;
  }
}

const ValidationIssueSchema = z.object({
  code: z.string().min(1),
  messageKey: z.string().optional(),
  severity: z.enum(IssueSeverity).default(IssueSeverity.UNSPECIFIED),
  target: z
    .custom<IssueTarget>(value => value instanceof IssueTarget)
    .default(() => IssueTarget.none()),
  params: z.record(z.string(), z.string()).default({}),
  defaultMessage: z.string().default(""),
});

export type ValidationIssueProps = z.input<typeof ValidationIssueSchema>;

/**
 * A blocking error, warning or impact of the feature. Issues come from the server
 * (response bodies, the `error-issues-bin` trailer) or from the client pre-checks.
 */
export class ValidationIssue {
  public readonly code: string;
  public readonly messageKey: string;
  public readonly severity: IssueSeverity;
  public readonly target: IssueTarget;
  public readonly params: Readonly<Record<string, string>>;
  public readonly defaultMessage: string;

  private constructor(props: z.infer<typeof ValidationIssueSchema>) {
    this.code = props.code;
    this.messageKey = props.messageKey || MESSAGE_KEY_PREFIX + props.code;
    this.severity = props.severity;
    this.target = props.target;
    this.params = Object.freeze({ ...props.params });
    this.defaultMessage = props.defaultMessage;
  }

  static of(props: ValidationIssueProps): ValidationIssue {
    return new ValidationIssue(ValidationIssueSchema.parse(props));
  }

  /** The code as a known `IssueCode`, or `undefined` for a code this front does not know. */
  knownCode(): IssueCode | undefined {
    return Object.values(IssueCode).find(code => code === this.code);
  }

  is(code: IssueCode): boolean {
    return this.code === code;
  }

  isBlocking(): boolean {
    return this.severity === IssueSeverity.BLOCKING;
  }

  isWarning(): boolean {
    return this.severity === IssueSeverity.WARNING;
  }

  param(name: string): string | undefined {
    return this.params[name];
  }

  /** A copy whose missing or empty params take the given values; received params win. */
  withParamDefaults(defaults: Readonly<Record<string, string>>): ValidationIssue {
    const params: Record<string, string> = { ...defaults };
    Object.entries(this.params).forEach(([name, value]) => {
      if (value) params[name] = value;
    });
    return new ValidationIssue({ ...this, params });
  }

  /** Category named by the issue: `params.category` first, then the target block. */
  category(): ExtraCategory | undefined {
    const fromParams = ExtraCategories.parse(this.param("category"));
    if (fromParams && fromParams !== ExtraCategory.NONE) {
      return fromParams;
    }
    return this.target.isBlockScoped() ? this.target.category : undefined;
  }

  targetsBlock(category: ExtraCategory): boolean {
    return this.target.targetsBlock(category);
  }

  targetsField(field: IssueField): boolean {
    return this.target.targetsField(field);
  }
}
